import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getSlides } from 'service/CatalogService';
import { getNews } from 'service/MediaService'
import styles from './EnterToSystemSection.module.scss'
import userInfo from 'utils/userInfo'

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent)
const isInStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true

// SVG placeholder пока загружаются картинки
const createPlaceholderSVG = (height = 342) => {
  const svg = `<svg width="100%" height="${height}px" xmlns="http://www.w3.org/2000/svg" style="display:block;">
    <rect width="100%" height="100%" fill="#2D2D2D" rx="0 0 10 10"/>
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

const EnterToSystemSection = () => {
  const user = userInfo()
  const navigate = useNavigate()

  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [iosGuideOpen, setIosGuideOpen] = useState(false)
  const [pwaInstalled, setPwaInstalled] = useState(
    () => isInStandalone() || !!localStorage.getItem('ncore_pwa_dismissed')
  )

  useEffect(() => {
    if (pwaInstalled) return
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e) }
    window.addEventListener('beforeinstallprompt', handler)
    window.addEventListener('appinstalled', () => setPwaInstalled(true))
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') { setPwaInstalled(true); localStorage.setItem('ncore_pwa_dismissed', '1') }
    setDeferredPrompt(null)
  }

  const handleDismissPwa = () => {
    localStorage.setItem('ncore_pwa_dismissed', '1')
    setPwaInstalled(true)
    setIosGuideOpen(false)
  }

  const defaultFrames = [createPlaceholderSVG(342)]
  const defaultFramesMobile = [createPlaceholderSVG(272)]

  // Слоганы для десктопа (длинные, мотивирующие)
  const desktopSlogans = [
    'Твой мир, твои правила — создавай, вдохновляй, побеждай',
    'Здесь начинается твоя легенда: играй, творит, доминируй',
    'Добро пожаловать в мир безграничных возможностей и эпических побед',
    'Создай свою историю: мерч, стиль, уникальность в одном месте',
    'Присоединяйся к элите: здесь рождаются чемпионы и лидеры',
    'Твоя платформа для побед: от идеи до триумфа',
    'Раскрой свой потенциал: сообщество, которое меняет правила игры',
    'Будь уникальным: эксклюзивный мерч и товары для настоящих гейминг-легенд',
    'Ломай стереотипы, создавай тренды — это твоё время сиять',
    'Место, где мечты становятся реальностью: играй на максимум',
    'Не просто платформа — это стиль жизни для тех, кто не сдаётся',
    'Твой путь к величию начинается здесь и сейчас',
    'Объединяйся с лучшими: вместе мы непобедимы',
    'Эксклюзивность и качество: только для тех, кто знает цену победе',
    'Делай заявку на будущее: инновации, страсть, результат',
    'Твоя арена, твои достижения — покажи миру, на что способен'
  ]

  // Слоганы для мобилки (короткие, ёмкие)
  const mobileSlogans = [
    'Твой мир, твои правила',
    'Легенды начинаются здесь',
    'Безграничные возможности',
    'Твой стиль, твоя игра',
    'Присоединяйся к элите',
    'Платформа для побед',
    'Раскрой потенциал',
    'Будь уникальным',
    'Создавай тренды',
    'Мечты в реальность',
    'Стиль жизни победителей',
    'Путь к величию',
    'Вместе непобедимы',
    'Качество для лучших',
    'Инновации и страсть',
    'Покажи своё мастерство'
  ]

  // Выбираем случайный индекс при загрузке компонента
  const [sloganIndex] = useState(() => Math.floor(Math.random() * desktopSlogans.length))

  // slidesData: array of { id, image, image_mobile }
  const [slidesData, setSlidesData] = useState(() =>
    defaultFrames.map((img, idx) => ({ id: `local-${idx}`, image: img, image_mobile: defaultFramesMobile[idx] || img }))
  )

  const [active, setActive] = useState(0)
  const intervalRef = useRef(null)
  const isPausedRef = useRef(false)
  const pointerDownRef = useRef(false)
  const startXRef = useRef(0)
  const currentXRef = useRef(0)
  const isDraggingRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)
  const [latestNews, setLatestNews] = useState([])
  const [isNewsLoading, setIsNewsLoading] = useState(true)

  // Fetch remote slides and replace defaults if available
  useEffect(() => {
    let mounted = true
    const tryFetch = async () => {
      try {
        const res = await getSlides()
        if (!mounted || !res) return
        // expect res to be array of { id, image, image_mobile }
        if (Array.isArray(res) && res.length) {
          const mapped = res.map((s) => ({ id: s.id, image: s.image, image_mobile: s.image_mobile || s.image }))
          setSlidesData(mapped)
          // reset active to 0 to avoid out-of-bounds
          setActive(0)
        }
      } catch (e) {
        // keep defaults on error
        // console.warn('Failed to load slides', e)
      }
    }
    tryFetch()
    return () => {
      mounted = false
    }
  }, [])

  // autoplay effect depends on slidesData length
  useEffect(() => {
    const start = () => {
      if (intervalRef.current) return
      intervalRef.current = setInterval(() => {
        if (!isPausedRef.current && slidesData.length > 0) setActive((s) => (s + 1) % slidesData.length)
      }, 8000)
    }

    // start autoplay only if we have more than 1 slide
    if (slidesData.length > 1) start()

    return () => clearInterval(intervalRef.current)
  }, [slidesData.length])

  useEffect(() => {
    let mounted = true

    const loadLatestNews = async () => {
      try {
        setIsNewsLoading(true)
        const response = await getNews({ page: 1, limit: 10, ordering: '-created_at' })
        if (!mounted) return

        const payload = response?.data
        const items = payload?.results || payload || []
        const normalized = (Array.isArray(items) ? items : []).slice(0, 10).map((item, index) => ({
          id: item.id || item.slug || `news-${index}`,
          slug: item.slug,
          title: item.title || item.name || 'Без названия',
          publishedAt: item.published_at || item.created_at || null,
        }))

        setLatestNews(normalized)
      } catch (error) {
        if (!mounted) return
        setLatestNews([])
      } finally {
        if (mounted) {
          setIsNewsLoading(false)
        }
      }
    }

    loadLatestNews()

    return () => {
      mounted = false
    }
  }, [])

  const formatNewsDate = (dateString) => {
    if (!dateString) return 'Недавно'
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return 'Недавно'
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
  }

  const shouldAnimateNews = latestNews.length > 4
  const newsFeedItems = shouldAnimateNews ? [...latestNews, ...latestNews] : latestNews

  const restartInterval = () => {
    if (intervalRef.current || slidesData.length <= 1) return
    intervalRef.current = setInterval(() => {
      if (!isPausedRef.current && slidesData.length > 0) setActive((s) => (s + 1) % slidesData.length)
    }, 8000)
  }

  const nextSlide = () => {
    setActive((s) => (s + 1) % Math.max(1, slidesData.length))
  }

  const prevSlide = () => {
    setActive((s) => (s - 1 + slidesData.length) % Math.max(1, slidesData.length))
  }

  const goTo = (index) => {
    // protect against out-of-bounds
    const idx = Math.max(0, Math.min(index, slidesData.length - 1))
    setActive(idx)
    // stop autoplay after manual change
    isPausedRef.current = true
    clearInterval(intervalRef.current)
    intervalRef.current = null
  }

  // pointer / touch handlers for drag/swipe
  const onPointerDown = (e) => {
    // support touch and mouse
    // If this is a pointer event from mouse, only start on left button (button === 0)
    if (e.pointerType === 'mouse' && typeof e.button === 'number' && e.button !== 0) return
    // prevent native image drag for mouse
    if (e.pointerType === 'mouse' && typeof e.preventDefault === 'function') e.preventDefault()
    const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX) ?? 0
    pointerDownRef.current = true
    startXRef.current = clientX
    currentXRef.current = clientX
    isDraggingRef.current = false
    setIsDragging(true)
    // pause autoplay while dragging
    isPausedRef.current = true
    clearInterval(intervalRef.current)
    intervalRef.current = null
  }

  const onPointerMove = (e) => {
    if (!pointerDownRef.current) return
    // prevent native drag behavior for mouse moves
    if (e.pointerType === 'mouse' && typeof e.preventDefault === 'function') e.preventDefault()
    const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX) ?? 0
    currentXRef.current = clientX
    const delta = clientX - startXRef.current
    const absDelta = Math.abs(delta)
    const threshold = 60
    // mark as dragging after small movement
    if (!isDraggingRef.current && absDelta > 8) {
      isDraggingRef.current = true
    }
    // if moved enough, trigger slide change and reset startX to allow multiple swipes in one drag
    if (absDelta >= threshold) {
      if (delta < 0) {
        nextSlide()
      } else {
        prevSlide()
      }
      // reset start point to allow subsequent swipes while holding
      startXRef.current = clientX
    }
  }

  const onPointerUp = () => {
    if (!pointerDownRef.current) return
    pointerDownRef.current = false
    isDraggingRef.current = false
    setIsDragging(false)
    // resume autoplay after interaction
    isPausedRef.current = false
    restartInterval()
  }

  const handleMouseEnter = () => {
    isPausedRef.current = true
  }

  const handleMouseLeave = () => {
    isPausedRef.current = false
    // restart interval
    if (!intervalRef.current && slidesData.length > 1) {
      intervalRef.current = setInterval(() => {
        if (!isPausedRef.current) setActive((s) => (s + 1) % slidesData.length)
      }, 8000)
    }
  }

  return (
    <div className={styles.section} id="enter">
      <div className={styles.heroRow}>
        <div className={styles.left_side}>


          <div
            className={`${styles.slider} ${isDragging ? styles.dragging : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onTouchStart={onPointerDown}
            onTouchMove={onPointerMove}
            onTouchEnd={onPointerUp}
          >
            <div className={styles.slides}>

              <picture className={styles.measurerPicture}>
                <source
                  srcSet={slidesData[active]?.image_mobile || slidesData[active]?.image || ''}
                  media="(max-width: 767px)"
                />
                <img src={slidesData[active]?.image || ''} alt="" className={styles.measurer} />
              </picture>
              {slidesData.map((slide, idx) => (
                <picture key={slide.id || idx} className={`${styles.slide} ${idx === active ? styles.active : ''}`}>
                  <source srcSet={slide.image_mobile || slide.image} media="(max-width: 767px)" />
                  <img src={slide.image} alt={`slide-${idx + 1}`} className={styles.icon} />
                </picture>
              ))}
            </div>

          </div>

          <div className={styles.dots}>
            {slidesData.map((_, idx) => (
              <button
                key={idx}
                className={`${styles.dot} ${idx === active ? styles.activeDot : ''}`}
                onClick={() => goTo(idx)}
                aria-label={`Перейти к слайду ${idx + 1}`}
              />
            ))}
          </div>
          <div className={styles.titleWithStats}>
            <h1 className={styles.desktopSlogan}>{desktopSlogans[sloganIndex]}</h1>
            <h1 className={styles.mobileSlogan}>{mobileSlogans[sloganIndex]}</h1>
          </div>
        </div>

        <aside className={styles.newsSide} aria-label="Последние новости">
          <div className={styles.newsRing}>
            <div className={styles.newsPanel}>
              <div className={styles.newsHeader}>
                <h3>Media новости</h3>
                <span>10 последних</span>
              </div>

              <div className={styles.newsViewport}>
                {isNewsLoading ? (
                  <div className={styles.newsEmptyState}>Загружаем новости...</div>
                ) : latestNews.length === 0 ? (
                  <div className={styles.newsEmptyState}>Пока нет новостей</div>
                ) : (
                  <div className={`${styles.newsTrack} ${shouldAnimateNews ? styles.newsTrackAnimated : ''}`}>
                    {newsFeedItems.map((item, index) => (
                      <article key={`${item.id}-${index}`} className={styles.newsItem}>
                        <p className={styles.newsItemTitle}>{item.title}</p>
                        <span className={styles.newsItemDate}>{formatNewsDate(item.publishedAt)}</span>
                      </article>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="button"
                className={styles.moreNewsButton}
                onClick={() => navigate('/services/media')}
              >
                Больше новостей
              </button>
            </div>
          </div>
        </aside>

        {/* ── PWA Install block (mobile only) ── */}
        {!pwaInstalled && (
          <div className={styles.pwaBlock}>
            {isIOS() ? (
              <>
                <button className={styles.pwaBtn} onClick={() => setIosGuideOpen(!iosGuideOpen)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3v13M7 9l5-6 5 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M4 15v5a1 1 0 001 1h14a1 1 0 001-1v-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                  </svg>
                  Добавить на главный экран
                </button>
                {iosGuideOpen && (
                  <div className={styles.iosSteps}>
                    <div className={styles.iosStep}>
                      <span className={styles.iosNum}>1</span>
                      <span>Нажми <span className={styles.iosTag}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M12 3v13M7 9l5-6 5 6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M4 15v5a1 1 0 001 1h14a1 1 0 001-1v-5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"/>
                        </svg>
                        Поделиться
                      </span> внизу Safari</span>
                    </div>
                    <div className={styles.iosStep}>
                      <span className={styles.iosNum}>2</span>
                      <span>Выбери <span className={styles.iosTag}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="3" width="18" height="18" rx="3" stroke="#fff" strokeWidth="2"/>
                          <path d="M12 8v8M8 12h8" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        На экран «Домой»
                      </span></span>
                    </div>
                    <div className={styles.iosStep}>
                      <span className={styles.iosNum}>3</span>
                      <span>Нажми <span className={styles.iosTagGreen}>Добавить</span></span>
                    </div>
                    <button className={styles.pwaDismiss} onClick={handleDismissPwa}>Больше не показывать</button>
                  </div>
                )}
              </>
            ) : deferredPrompt ? (
              <button className={styles.pwaBtn} onClick={handleAndroidInstall}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 3v13M6 12l6 7 6-7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4 20h16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
                </svg>
                Установить приложение
              </button>
            ) : null}
          </div>
        )}
      </div>

    </div>
  )
}

export default EnterToSystemSection
